import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Route } from "react-router-dom";

import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import CssBaseline from "@material-ui/core/CssBaseline";

import ErrorModal from "@meetyoo/components/src/components/Modal/ErrorModal";
import { CONTENT_MARGIN_TOP_DESKTOP } from "../../../constants/ui";
import {
  PM_CONFERENCES_PATH,
  BILLING_LEDGER_ENTRIES_PATH,
  EVENT_BILLING_PATH,
  DOCUMENT_SENDING_ENTRIES,
  BILLING_EVN_CAPS,
  PROJECTMAIL,
} from "../../../constants/router";

import Appbar from "../../Navigation/Appbar";
import Drawer from "../../Navigation/Drawer";
import PMConferences from "../PMConferences";
import PMTabs from "../PMTabs";
import BillingLedgerEntries from "../BillingLedgerEntries";
import BillingLedgerEntry from "../BillingLedgerEntry";
import BillingEvent from "../BillingEvent";
import BillingDocumentSending from "../BillingDocumentSending";
import BillingEvnCaps from "../BillingEvnCaps";
import { EmailTicketSytemTable, EmailTicketSytemTableClosed } from "../EmailTicketSystem";
import { setNotificationPermission } from "../../../actions/oauth";
import HomePicture from "../../../images/Home-Image-ETM.jpg";

const drawerShrinkWidth = 73;

const useStyles = makeStyles(theme => ({
  content: {
    height: "calc(100vh - 64px)",
    backgroundColor: "#f4f6f8",
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  contentDesktop: {
    marginTop: CONTENT_MARGIN_TOP_DESKTOP,
    marginLeft: drawerShrinkWidth,
  },
  contentShift: {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: props => props.drawerWidth,
  },
  image: {
    backgroundImage: `url(${HomePicture})`,
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    backgroundPosition: "center",
    height: "100%",
  },
}));

export const MainComponent = ({
  sideDrawerOpen,
  notificationPermission,
  setNotificationPermissionFn,
  sumOfTickets,
  tokenError,
  translate,
  drawerWidth,
  webSocketError,
}) => {
  const [errorMessage, setErrorMessageReload] = useState(translate.authorizationError.replace("{0}", "10"));
  useEffect(() => {
    if (tokenError) {
      let sekunden = 9;
      setInterval(() => {
        setErrorMessageReload(translate.authorizationError.replace("{0}", sekunden));
        sekunden -= 1;
      }, 1000);
    }
  }, [tokenError, setErrorMessageReload, translate]);

  const [webSocketErrorMessage, setWebSocketErrorMessage] = useState(translate.webSocketError.replace("{0}", "10"));
  useEffect(() => {
    if (webSocketError) {
      let sekunden = 9;
      setInterval(() => {
        setWebSocketErrorMessage(translate.webSocketError.replace("{0}", sekunden));
        sekunden -= 1;
      }, 1000);
    }
  }, [webSocketError, setWebSocketErrorMessage, translate]);

  const classes = useStyles({ drawerWidth });
  useEffect(() => {
    const requestPermission = async () => {
      const permission = await Notification.requestPermission();
      setNotificationPermissionFn(permission === "granted");
    };
    if (notificationPermission === undefined) {
      requestPermission();
    }
  }, [notificationPermission, setNotificationPermissionFn, sumOfTickets]);
  return [
    <div className={classes.root} key="main">
      <CssBaseline />
      <Appbar />
      <Drawer />
      <main
        className={clsx(classes.content, {
          [classes.contentShift]: sideDrawerOpen,
          [classes.contentDesktop]: true,
        })}
      >
        <Route path={PM_CONFERENCES_PATH} exact component={PMConferences} />
        <Route path={`${PM_CONFERENCES_PATH}/:id`} component={PMTabs} />
        <Route path={BILLING_LEDGER_ENTRIES_PATH} exact component={BillingLedgerEntries} />
        <Route path={`${BILLING_LEDGER_ENTRIES_PATH}/:id`} component={BillingLedgerEntry} />
        <Route path={EVENT_BILLING_PATH} component={BillingEvent} />
        <Route path={DOCUMENT_SENDING_ENTRIES} component={BillingDocumentSending} />
        <Route path={BILLING_EVN_CAPS} component={BillingEvnCaps} />
        <Route path={`${PROJECTMAIL}/webcast`}>
          <EmailTicketSytemTable title={translate.webcast_tickets} type="webcast" />
        </Route>
        <Route path={`${PROJECTMAIL}/event`}>
          <EmailTicketSytemTable title={translate.event_tickets} type="event" />
        </Route>
        <Route path={`${PROJECTMAIL}/own`}>
          <EmailTicketSytemTable title={translate.own_tickets} type="own" />
        </Route>
        <Route path={`${PROJECTMAIL}/urgent`}>
          <EmailTicketSytemTable title={translate.urgent_tickets} type="urgent" />
        </Route>
        <Route path={`${PROJECTMAIL}/closed`}>
          <EmailTicketSytemTableClosed title={translate.closed_tickets} pageing />
        </Route>
        <Route path={`${PROJECTMAIL}/ticketHistory`}>
          <EmailTicketSytemTable title={translate.latest_tickets} type="ticketHistory" />
        </Route>
        <Route path="/" exact>
          <div className={classes.image} />
        </Route>
      </main>
    </div>,
    <ErrorModal
      key="error"
      handleCloseErrorModal={() => window.location.reload()}
      openErrorModal={tokenError}
      errorMessage={errorMessage}
    />,
    <ErrorModal
      key="websocket-error"
      handleCloseErrorModal={() => window.location.reload()}
      openErrorModal={webSocketError}
      errorMessage={webSocketErrorMessage}
    />,
  ];
};

MainComponent.defaultProps = {
  notificationPermission: undefined,
  sumOfTickets: {},
};

MainComponent.propTypes = {
  sideDrawerOpen: PropTypes.bool.isRequired,
  notificationPermission: PropTypes.bool,
  setNotificationPermissionFn: PropTypes.func.isRequired,
  sumOfTickets: PropTypes.object,
  tokenError: PropTypes.bool.isRequired,
  translate: PropTypes.object.isRequired,
  drawerWidth: PropTypes.number.isRequired,
  webSocketError: PropTypes.bool.isRequired,
};

const mapDispatchToProps = dispatch => ({
  setNotificationPermissionFn: permission => dispatch(setNotificationPermission(permission)),
});

const mapStateToProps = state => ({
  sideDrawerOpen: state.ui.sideDrawerOpen,
  notificationPermission: state.oauth.notificationPermission,
  sumOfTickets: state.projectmail.sumOfTickets,
  tokenError: state.oauth.tokenError,
  translate: state.ui.translate,
  drawerWidth: state.ui.sideDrawerWidth,
  webSocketError: state.projectmail.webSocketError,
});

const Main = connect(
  mapStateToProps,
  mapDispatchToProps
)(MainComponent);

export default Main;
